import { useEffect, useState } from 'react';
import { Link, useLoaderData } from 'react-router-dom';
import { Avatar, List, Skeleton } from 'antd';
import { ClockCircleOutlined, SwapRightOutlined } from '@ant-design/icons';
import {
  getCampaign,
  getCampaignLeaderboard,
  getCampaignRelayStatus,
} from '../../api/challenges';
import { formatDate } from '../../utils/date';
import { calculatePace, round } from '../../utils/format';
import CampaignRegistrationPanel from './CampaignRegistrationPanel';
import RelayLeaderboard from './RelayLeaderboard';
import './Campaign.css';

export async function loader({ params }) {
  const campaign = await getCampaign(params.campaignId);
  if (!campaign) {
    throw new Response('', { status: 404, statusText: 'Not Found' });
  }
  return { campaign, id: params.campaignId, name: params.name };
}

export async function action() {}

export default function CampaignDetail() {
  const { campaign, id, name } = useLoaderData();
  const [leaderboard, setLeaderboard] = useState([]);
  const [relayStatus, setRelayStatus] = useState(null);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('leaderboard'); // 'leaderboard' | 'relay'

  useEffect(() => {
    let ignore = false;

    async function load() {
      const [board, relay] = await Promise.all([
        getCampaignLeaderboard({ campaignId: id }),
        getCampaignRelayStatus(id),
      ]);
      if (!ignore) {
        setLeaderboard(board);
        setRelayStatus(relay);
        // Default to relay tab if relay mode is active
        if (relay?.is_relay_mode) setActiveTab('relay');
      }
    }

    load().catch((err) => setMessage(err.message));
    return () => { ignore = true; };
  }, [id]);

  const isRelayMode = Boolean(relayStatus?.is_relay_mode);

  return (
    <article className="campaign-detail">
      {/* Hero */}
      <section className="campaign-detail-hero htv-card">
        {campaign.bannerUrl && <img alt="" src={campaign.bannerUrl} />}
        <div className="campaign-detail-copy">
          <p className="htv-muted">
            Hành trình nội bộ{isRelayMode ? ' · 🏃 Relay Challenge' : ''}
          </p>
          <h1>{campaign.name}</h1>
          <p>{campaign.description}</p>
          <div className="campaign-card-meta">
            <span>
              <ClockCircleOutlined /> Đăng ký:{' '}
              {formatDate(campaign.registerStart)} — {formatDate(campaign.registerEnd)}
            </span>
            <span>
              <ClockCircleOutlined /> Diễn ra:{' '}
              {formatDate(campaign.startDate)} — {formatDate(campaign.endDate)}
            </span>
            {isRelayMode ? (
              <span>🎯 Mục tiêu: {relayStatus.relay_target_km?.toLocaleString()} km · Cap {relayStatus.relay_daily_cap_km} km/ngày</span>
            ) : (
              <span>
                <SwapRightOutlined /> Pace:{' '}
                {!campaign.minPace && !campaign.maxPace
                  ? 'Không giới hạn'
                  : `${campaign.minPace} – ${campaign.maxPace}`}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Registration panel */}
      <CampaignRegistrationPanel
        campaign={campaign}
        campaignId={id}
        mapLink={`/campaign/${name}/${id}/map`}
      />

      {/* Leaderboard section — tab switcher when relay */}
      <section className="campaign-team htv-card">
        {isRelayMode ? (
          <>
            {/* Tab bar */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '1px solid #f0f0f0' }}>
              <TabButton
                active={activeTab === 'relay'}
                onClick={() => setActiveTab('relay')}
              >
                🏃 Relay Leaderboard
              </TabButton>
              <TabButton
                active={activeTab === 'leaderboard'}
                onClick={() => setActiveTab('leaderboard')}
              >
                📊 Individual
              </TabButton>
            </div>

            {activeTab === 'relay' && (
              <RelayLeaderboard campaignId={id} />
            )}

            {activeTab === 'leaderboard' && (
              <IndividualLeaderboard leaderboard={leaderboard} message={message} />
            )}
          </>
        ) : (
          <>
            <h2>Đội hình / Bảng tiến độ</h2>
            <IndividualLeaderboard leaderboard={leaderboard} message={message} />
          </>
        )}
      </section>
    </article>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        padding: '10px 20px',
        fontWeight: active ? 700 : 400,
        fontSize: 14,
        color: active ? '#1677ff' : '#595959',
        borderBottom: active ? '2px solid #1677ff' : '2px solid transparent',
        cursor: 'pointer',
        transition: 'all 0.15s',
        marginBottom: -1,
      }}
    >
      {children}
    </button>
  );
}

function IndividualLeaderboard({ leaderboard, message }) {
  return (
    <>
      {message && <p className="htv-muted">{message}</p>}
      <List
        itemLayout="horizontal"
        dataSource={leaderboard}
        renderItem={(item) => (
          <List.Item
            actions={[
              <span
                key="distance"
                style={{
                  color:
                    item.distance_register > round(item.total_distance / 1000)
                      ? '#EA4710'
                      : '#52c41a',
                }}
              >
                {round(item.total_distance / 1000)}
                {item.distance_register ? `/${item.distance_register}` : ''} km
              </span>,
              <span key="pace">
                {calculatePace(item.total_moving_time, item.total_distance)}
              </span>,
            ]}
          >
            <Skeleton avatar title={false} loading={item.loading} active>
              <List.Item.Meta
                avatar={<Avatar src={item.avatar} />}
                title={
                  <Link to={`/athlete/${item.strava_id || item.user_id}`}>
                    {item.fullname}
                  </Link>
                }
                description={`BIB: ${item.strava_id || item.user_id || 'N/A'}`}
              />
            </Skeleton>
          </List.Item>
        )}
      />
    </>
  );
}
